import { _ } from "libs";
import React, { FC, memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Championship, Card, ChampionshipOptInContext } from "../../types";
import moment, { now } from "moment";
import { flashError, getChampionshipOptInContext, joinFreeChampionship, joinPaidChampionship } from "../../actions";
import PaymentModal from "../paymentModal";
import Currency from "../currency";
import Loader from "../loader";
import Progressbar from "./progressbar";
import ChampionshipContext from "../../contexts/championshipContext";
import { NavLink } from "react-router-dom";

const ChampionshipAbout: FC = () => {
	const { championship, context } = useContext(ChampionshipContext);

	const [contextLoaded, setContextLoaded] = useState(false);
	const [optInFee, setOptInFee] = useState(championship.settings.optInFee ?? 0);
	const [trialContext, setTrialContext] = useState(null as any);

	if (!championship) {
		return null;
	}

	const hasActiveOrExpiredTrial = (): boolean => {
		return championship?.settings.trial?.hasActiveTrial || championship?.settings.trial?.hasExpiredTrial;
	};

	const contextResult = useMemo(() => {
		//we are checking for promos here
		if (championship.settings.optInFee === 0) {
			setContextLoaded(true);
			return Promise.resolve(null);
		}

		if (championship.optedIn && !hasActiveOrExpiredTrial()) {
			setContextLoaded(true);
			return Promise.resolve(null);
		}

		if (context) {
			setContextLoaded(false);
			return context(null).then((c: any) => {
				setContextLoaded(true);
				calculateOptInFee(c);
				setTrialContext(c?.trial);
				return c;
			});
		} else {
			setContextLoaded(true);
			return Promise.resolve(null);
		}
	}, [championship]);

	const calculateOptInFee = (context) => {
		if (!contextLoaded) {
			setOptInFee(championship.settings.optInFee);
		}

		if (context?.promotions?.[0]?.discount) {
			setOptInFee(championship.settings.optInFee - context.promotions[0].discount);
		} else {
			setOptInFee(championship.settings.optInFee);
		}
	};

	return <OptIn contextLoaded={contextLoaded} optInFee={optInFee} contextResult={contextResult} />;
};

const OptIn = ({ contextLoaded, optInFee, contextResult }: { contextLoaded: boolean; optInFee; contextResult }) => {
	const { championship, onJoinClick, modal, onPayClicked, context } = useContext(ChampionshipContext);
	let isTournament = championship.championshipType === "tournament";
	let canJoin = false;
	let isSoldOut = isTournament && championship.players >= championship.settings.maxParticipants;
	if (championship.optedIn) {
		canJoin = false;
	} else if (isTournament) {
		if (championship.settings.maxParticipants <= championship.players) {
			canJoin = false;
		}
		if (championship.settings.optInDeadline) {
			canJoin = championship.settings.optInDeadline > Date.now() / 1000;
		} else {
			canJoin = championship.endDateTime > Date.now() / 1000;
		}
	} else {
		canJoin = championship.endDateTime > Date.now() / 1000;
	}
	return (
		<div className="nBlock aboutChampionship">
			<h2 className="mt-2 mb-3">{championship.name}</h2>
			{championship.description ? <p>{championship.description}</p> : null}
			<div className="table">
				{isTournament ? (
					<>
						<div className="cell">
							<div className="tableRow">
								<div className="tableRowLabel">{_("championship.about.date")}</div>
								<div className="tableRowValue">
									{moment.unix(championship.startDateTime).format("ddd, MMM D")}
								</div>
							</div>
						</div>
						<div className="cell">
							<div className="tableRow">
								<div className="tableRowLabel">{_("championship.about.time")}</div>
								<div className="tableRowValue">
									{moment.unix(championship.startDateTime).format("HH:mm")}
									&nbsp;&mdash;&nbsp;
									{moment.unix(championship.endDateTime).format("HH:mm")}
								</div>
							</div>
						</div>
					</>
				) : (
					<div className="cell">
						<div className="tableRow">
							<div className="tableRowLabel">{_("championship.about.duration")}</div>
							<div className="tableRowValue">
								{moment.unix(championship.startDateTime).format("MMM D, YYYY")}
								&nbsp;&mdash;&nbsp;
								{moment.unix(championship.endDateTime).format("MMM D, YYYY")}
							</div>
						</div>
					</div>
				)}
				<div className="cell">
					<div className="tableRow">
						<div className="tableRowLabel">{_("championship.about.price")}</div>
						<div className="tableRowValue">
							{championship.settings.optInFee === 0 ? (
								<>{_("label.free")}</>
							) : contextLoaded ? (
								<Currency amount={optInFee} currency={championship.settings.currency} />
							) : (
								<Loader />
							)}
						</div>
					</div>
				</div>
				{isTournament && (
					<div className="cell">
						<div className="tableRow">
							<div className="tableRowLabel">{_("championship.about.city")}</div>
							<div className="tableRowValue">{championship.settings?.city}</div>
						</div>
					</div>
				)}
				{isTournament && (
					<div className="cell">
						<div className="tableRow">
							<div className="tableRowLabel">{_("championship.about.club")}</div>
							<div className="tableRowValue">
								<NavLink to={`/club/${championship.settings?.club?.clubId}`} className="link">
									{championship.settings?.club?.name}
								</NavLink>
							</div>
						</div>
					</div>
				)}
				{isTournament && (
					<div className="cell">
						<div className="tableRow">
							<div className="tableRowLabel">{_("championship.about.players")}</div>
							<div className="tableRowValue">
								{championship.players}/{championship.settings.maxParticipants}
							</div>
						</div>
					</div>
				)}
			</div>
			{championship.settings.readMoreUrl ? (
				<a
					className="button light fullWidth"
					href={championship.settings.readMoreUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					{isTournament ? _("championship.about.announcementBtn") : _("championship.about.learnMoreBtn")}
				</a>
			) : null}
			{championship.optedIn === false &&
			contextLoaded &&
			championship?.settings?.trial?.canStartTrial === true &&
			!championship?.settings?.trial?.cardForTrial ? (
				<div className="button fullWidth" onClick={() => onJoinClick()}>
					{_("championship.trial.joinBtn")}
				</div>
			) : null}
			{canJoin &&
			championship.optedIn === false &&
			contextLoaded &&
			(championship?.settings?.trial?.canStartTrial === false ||
				(championship?.settings?.trial?.canStartTrial === true &&
					championship?.settings?.trial?.cardForTrial)) ? (
				<>
					{!isTournament || (isTournament && !isSoldOut) ? (
						<div className="button fullWidth" onClick={() => onJoinClick()}>
							{isTournament
								? _("championship.about.registerBtn")
								: championship?.settings?.trial?.canStartTrial
								? _("championship.trial.joinBtn")
								: _("championship.about.joinChampionship")}
						</div>
					) : null}
					{championship.settings.optInFee > 0 ? (
						<PaymentModal
							ref={modal}
							description={championship.name}
							amount={championship.settings.optInFee as number}
							currency={championship.settings.currency}
							onPayClicked={onPayClicked}
							context={context}
							precalculatedContext={contextResult}
							startTrial={championship?.settings?.trial?.canStartTrial}
							buttonLabel={
								championship?.settings?.trial?.canStartTrial ? _("championship.trial.joinBtn") : null
							}
						/>
					) : null}
				</>
			) : null}
			{championship.settings.optInDeadline && !isSoldOut && championship.endDateTime > Date.now() / 1000 ? (
				<p className="small note mt-2">
					{_("championship.about.registerDeadline", null, {
						date: moment.unix(championship.settings.optInDeadline).format("ddd, MMM D"),
						time: moment.unix(championship.settings.optInDeadline).format("HH:mm"),
					})}
				</p>
			) : null}
		</div>
	);
};

const Trial = ({
	contextLoaded,
	trialContext,
	contextResult,
}: {
	contextLoaded: boolean;
	trialContext;
	contextResult;
}) => {
	const { championship, modal, onPayClicked, onJoinClick, context } = useContext(ChampionshipContext);

	const now: number = (Date.now() / 1000) | 0;
	const totalDays = Math.floor(
		(championship.settings.trial?.trialExpiresAt - championship.optedInAt) / (60 * 60 * 24)
	);
	const daysLeft = Math.floor((championship.settings.trial?.trialExpiresAt - now) / (60 * 60 * 24));
	return (
		<div className="nBlock trial">
			<div className="image" />
			<div className="title">{_("championship.trial.heading")}</div>
			{championship.settings.trial?.hasActiveTrial ? (
				<>
					<div className="progressBarLabel mt-3">
						<div className="icon small-clock-fill-orange" />
						<div className="label">{_("championship.trial.daysRemaining", null, { days: daysLeft })}</div>
					</div>
					<Progressbar current={totalDays - daysLeft} max={totalDays} className="mb-3" />
				</>
			) : null}
			{championship.settings.trial?.hasExpiredTrial ? (
				<div className="progressBarLabel mt-3">
					<div className="icon small-alert-fill-red" />
					<div className="label color-red">{_("championship.trial.expired")}</div>
				</div>
			) : null}
			{contextLoaded && trialContext && (
				<>
					<p>{trialContext?.expiryInformationText}</p>
					<p>{trialContext?.upgradePriceInformationText}</p>
				</>
			)}

			<button className="button w-full mt-3" onClick={() => onJoinClick()}>
				{_("championship.trial.purchaseSeasonBtn")}
			</button>
			{championship.settings.optInFee > 0 ? (
				<PaymentModal
					ref={modal}
					description={championship.name}
					amount={championship.settings.optInFee as number}
					currency={championship.settings.currency}
					onPayClicked={onPayClicked}
					context={context}
					precalculatedContext={contextResult}
				/>
			) : null}
		</div>
	);
};

export default memo(ChampionshipAbout);
